import { useGPFormContext } from '@/contexts/GPFormContext';
import { PaginationResponse } from '@/requests/types';
import { Box, Grid, Pagination, Skeleton, Typography } from '@mui/material';
import { GridSize } from '@mui/material/Grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SWRResponse } from 'swr';
import GPButton from './GPButton';

const LoadingSkeleton = () => (
  <Skeleton variant="rectangular" sx={{ width: '292px', height: '290px', borderRadius: '16px' }} />
);

type NoDataOverlayProps = {
  hint?: string;
};

const NoDataOverlay = ({ hint }: NoDataOverlayProps) => {
  const filterContext = useGPFormContext();
  const { t } = useTranslation();

  const handleClearFilters = () => {
    if (!filterContext) return;

    const { reset, defaultValues, handleSubmit, onSubmit } = filterContext;

    reset(defaultValues);
    handleSubmit(onSubmit)();
  };

  return (
    <Box>
      <Typography variant="h3">{t('No results found')}</Typography>
      {hint && <Typography>{hint}</Typography>}

      {filterContext?.modified.size > 0 && (
        <Box sx={{ mt: 2 }}>
          <GPButton variant="outlined" size="small" onClick={handleClearFilters}>
            {t('Clear filters')}
          </GPButton>
        </Box>
      )}
    </Box>
  );
};

type GPCardViewProps<T extends string, U extends { unique_id: string }> = {
  id: string;
  response:
    | SWRResponse<PaginationResponse<T, U> | null, unknown>
    | SWRResponse<PaginationResponse<T, U>, unknown>
    | SWRResponse<null, unknown>;
  wrapper: T;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  itemOptions?: Record<string, unknown>;
  ItemComponent: React.ElementType;
  itemHeight?: number;
  noResultsHint?: string;
  disablePagination?: boolean;
  LastItemComponent?: React.ElementType;
  size?: {
    xs?: GridSize;
    sm?: GridSize;
    md?: GridSize;
    lg?: GridSize;
  };
};

const GPCardView = <T extends string, U extends { unique_id: string }>({
  id,
  response,
  wrapper,
  page,
  setPage,
  noResultsHint,
  disablePagination,
  ItemComponent,
  itemOptions,
  LastItemComponent,
  size = {
    xs: 3,
    sm: 2,
    md: 2,
    lg: 2,
  },
}: GPCardViewProps<T, U>) => {
  const { data, isValidating } = response;
  const items = data?.[wrapper];
  const totalData = data?.meta?.total ?? 0;
  const pageCount = data?.meta?.last_page ?? 0;
  const noData = !isValidating && totalData === 0;

  const formContext = useGPFormContext();

  React.useEffect(() => {
    setPage(1);
  }, [formContext?.valueState]);

  return (
    <Box id={id}>
      {!noData && (
        <Box>
          <Grid container spacing={2}>
            {items?.length ? (
              <>
                {items.map(item => (
                  <Grid id="grid-card" key={item.unique_id} size={size}>
                    <ItemComponent {...item} {...itemOptions} />
                  </Grid>
                ))}

                {page === pageCount && LastItemComponent && (
                  <Grid size={size}>
                    <LastItemComponent />
                  </Grid>
                )}
              </>
            ) : page === pageCount && LastItemComponent ? (
              <Grid size={size}>
                <LastItemComponent />
              </Grid>
            ) : (
              [...Array.from(Array(5).keys())].map(i => (
                <Grid key={i} size={size}>
                  <LoadingSkeleton />
                </Grid>
              ))
            )}
          </Grid>

          {!!pageCount && pageCount > 1 && !disablePagination && (
            <Box
              sx={{
                display: 'flex',
                width: 1,
                justifyContent: 'center',
                mt: 2,
              }}
            >
              <Pagination
                count={pageCount}
                page={page}
                onChange={(e, _page) => {
                  setPage(_page);

                  const top = document.querySelector(`#${id}`);

                  if (top) {
                    const y = top.getBoundingClientRect().top + window.pageYOffset - 200;
                    window.scrollTo({ top: y });
                  }
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {noData &&
        (LastItemComponent ? <LastItemComponent /> : <NoDataOverlay hint={noResultsHint} />)}
    </Box>
  );
};

export default GPCardView;
